// The in-memory twin of the file service (TASK-FILES; contracts `api/files.ts`, services/api
// `files/service.ts` and `routes/files.ts`), with the API's rules:
// - a client viewer has no work access (403); the rest see a task's files as they see the task
//   (the Work twin's tasks; every member reads every live task there);
// - an upload is multipart `file` (or `text` + `file_name`) onto a task or one of its comments,
//   into a folder (`folder_id`) or a `/` chain (`folder_path`) made as needed, case-insensitively;
//   the type is sniffed from the bytes (`@companyos/domain-work/files`, the API's own code); the
//   cap is 25 MB;
// - a rename with a `/` moves the file into that chain; a delete is a trash (30 days); a restore
//   lands at the root when its folder is gone; Empty trash purges; deleting a folder trashes its
//   subtree's files to the root; covers toggle;
// - bytes only through `files.content` (`?download=true`, `?thumb=true`: here an image's own
//   bytes, no thumbnail for a PDF); `files.text` decodes text files (no PDF extraction here);
// - the agent tools (Work OS shapes) answer the same way for the signed-in caller;
// - every write is a `task.file_changed` / `task.file_folder_changed` / `task.trash_emptied`
//   event in the task's history (the Work twin's Activity).
// Seeded: Acme's "Screen 12 backend applicants" holds a brief (PDF, in "Briefs"), the pipeline
// chart (PNG), the candidates CSV and Riley's shortlist (Markdown, uploaded by the agent).
// `fake-files-contract.test.ts` holds it to the contracts.
import {
  cleanFileName,
  contentDisposition,
  previewOf,
  servedType,
  sniffMime,
  textual,
  thumbable,
} from "@companyos/domain-work/files";
import type { OperationOutput } from "@companyos/sdk";
import {
  type Ctx,
  type Handler,
  json,
  notFound,
  type Principal,
  problem,
  validation,
} from "./fake";
import { CANDIDATES_CSV, chartPng, SHORTLIST_MD, tinyPdf } from "./fake-files-data";
import type { WorkBridge } from "./fake-work";

type FileItem = OperationOutput<"files.get">;
type FolderItem = OperationOutput<"files.folders">["data"][number];
type Event = Parameters<WorkBridge["recordTaskEvent"]>[4];

type Stored = Omit<
  FileItem,
  "uploader" | "preview" | "has_thumb" | "folder_path" | "task" | "message_id" | "purge_at"
> & { body: Uint8Array };
type Folder = Omit<FolderItem, "live_files">;

const DAY = 86_400_000;
const MAX_BYTES = 25 * 1024 * 1024;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type FilesDeps = {
  now: () => Date;
  work: () => WorkBridge | null;
  /** A membership's name and kind (people from the accounts, agents from the DEs). */
  nameOf: (
    companyId: string,
    membershipId: string,
  ) => { name: string; kind: "person" | "digital_employee" | "personal_agent" } | null;
  /** Riley (the seeded agent uploader) and the company owner, for the seed. */
  seedMembers: (companyId: string) => { agent: string | null; owner: string | null };
};

export function createFilesFake(deps: FilesDeps) {
  const files = new Map<string, Stored[]>();
  const folders = new Map<string, Folder[]>();
  const seeded = new Set<string>();
  const stamp = () => deps.now().toISOString();
  const forbidden = (detail = "Forbidden") => problem(403, "forbidden", "Forbidden", detail);

  const filesOf = (co: string) => {
    seed(co);
    let l = files.get(co);
    if (!l) {
      l = [];
      files.set(co, l);
    }
    return l;
  };
  const foldersOf = (co: string) => {
    seed(co);
    let l = folders.get(co);
    if (!l) {
      l = [];
      folders.set(co, l);
    }
    return l;
  };
  const meOf = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? null;
  const reader = (p: Principal | null): p is Principal =>
    p?.roles.some((r) => r !== "client_viewer") === true;
  const actorOf = (p: Principal | null): Event => {
    const m = p ? meOf(p) : null;
    return m ? { kind: "person", membership_id: m } : { kind: "system" };
  };
  const taskOf = (co: string, id: string | null | undefined) =>
    id ? (deps.work()?.taskOf(co, id) ?? null) : null;

  function pathOf(co: string, id: string | null): string {
    const all = folders.get(co) ?? [];
    const names: string[] = [];
    let cur = id ? all.find((f) => f.id === id) : undefined;
    for (let g = 0; cur && g < 64; g++) {
      names.unshift(cur.name);
      const parent: string | null = cur.parent_id;
      cur = parent ? all.find((f) => f.id === parent) : undefined;
    }
    return names.join("/");
  }

  function item(co: string, f: Stored): FileItem {
    const { body: _b, ...row } = f;
    const who = deps.nameOf(co, f.uploader_membership_id);
    const t = taskOf(co, f.task_id);
    return {
      ...row,
      uploader: {
        membership_id: f.uploader_membership_id,
        kind: who?.kind ?? "person",
        name: who?.name ?? "Someone",
      },
      preview: previewOf(f.mime),
      has_thumb: thumbable(f.mime) && previewOf(f.mime) === "image",
      folder_path: pathOf(co, f.folder_id),
      task: t ? { id: t.id, title: t.title, board_id: t.board_id, number: t.number } : null,
      message_id: f.subject_kind === "message" ? f.subject_id : null,
      purge_at: f.deleted_at
        ? new Date(new Date(f.deleted_at).getTime() + 30 * DAY).toISOString()
        : null,
    };
  }

  function emit(
    co: string,
    taskId: string,
    type: "task.file_changed" | "task.file_folder_changed" | "task.trash_emptied",
    payload: Record<string, unknown>,
    actor: Event,
  ) {
    const t = taskOf(co, taskId);
    deps
      .work()
      ?.recordTaskEvent(co, taskId, type, { board_id: t?.board_id ?? null, ...payload }, actor);
  }
  const fileEvent = (
    co: string,
    f: Stored,
    op: string,
    actor: Event,
    fromName: string | null = null,
  ) =>
    f.task_id &&
    emit(
      co,
      f.task_id,
      "task.file_changed",
      {
        attachment_id: f.id,
        op,
        file_name: f.file_name,
        from_name: fromName,
        folder_id: f.folder_id,
        mime: f.mime,
        bytes: f.bytes,
        message_id: f.subject_kind === "message" ? f.subject_id : null,
      },
      actor,
    );

  function makeFolder(
    co: string,
    taskId: string,
    name: string,
    parent: string | null,
    actor: Event,
  ) {
    const f: Folder = {
      company_id: co,
      id: crypto.randomUUID(),
      task_id: taskId,
      name,
      parent_id: parent,
      actor_kind: actor.kind === "person" ? "person" : "system",
      actor_membership_id: actor.kind === "person" ? (actor.membership_id ?? null) : null,
      created_at: stamp(),
      updated_at: stamp(),
    };
    foldersOf(co).push(f);
    emit(
      co,
      taskId,
      "task.file_folder_changed",
      { folder_id: f.id, op: "added", name, parent_id: parent, file_count: 0 },
      actor,
    );
    return f;
  }

  function ensurePath(co: string, taskId: string, chain: string[], actor: Event): string | null {
    let parent: string | null = null;
    for (const raw of chain.map((s) => s.trim()).filter(Boolean)) {
      const hit = foldersOf(co).find(
        (f) =>
          f.task_id === taskId &&
          f.parent_id === parent &&
          f.name.toLowerCase() === raw.toLowerCase(),
      );
      parent = hit ? hit.id : makeFolder(co, taskId, raw.slice(0, 120), parent, actor).id;
    }
    return parent;
  }

  function store(
    co: string,
    taskId: string,
    input: {
      bytes: Uint8Array;
      name: string;
      claimed?: string | null;
      folderId?: string | null;
      folderPath?: string | null;
      messageId?: string | null;
      uploader: string;
      at?: string;
    },
    actor: Event,
  ): Stored | Response {
    if (input.bytes.byteLength === 0) return validation("file: the file is empty");
    if (input.bytes.byteLength > MAX_BYTES)
      return problem(
        413,
        "payload_too_large",
        "Payload too large",
        "File too large (workspace limit is 25 MB)",
      );
    const parts = input.name.split("/");
    const fileName = cleanFileName(parts.pop() ?? "");
    let folderId: string | null = null;
    if (input.folderId) {
      const f = foldersOf(co).find((x) => x.id === input.folderId && x.task_id === taskId);
      if (!f) return validation("folder_id: not a folder of this task");
      folderId = f.id;
    }
    const chain = [...(input.folderPath ? input.folderPath.split("/") : []), ...parts];
    if (!folderId && chain.some((s) => s.trim())) folderId = ensurePath(co, taskId, chain, actor);
    const at = input.at ?? stamp();
    const id = crypto.randomUUID();
    const f: Stored = {
      company_id: co,
      id,
      object_key: `files/${co}/${id}`,
      file_name: fileName,
      mime: sniffMime(input.bytes, fileName, input.claimed),
      bytes: input.bytes.byteLength,
      uploader_membership_id: input.uploader,
      subject_kind: input.messageId ? "message" : "task",
      subject_id: input.messageId ?? taskId,
      state: "ready",
      folder_id: folderId,
      is_cover: false,
      task_id: taskId,
      thread_id: null,
      provenance: null,
      width: null,
      height: null,
      derivatives_at: null,
      deleted_at: null,
      deleted_by_membership_id: null,
      actor_kind:
        actor.kind === "person" || actor.kind === "digital_employee" ? actor.kind : "system",
      actor_membership_id: "membership_id" in actor ? (actor.membership_id ?? null) : null,
      created_at: at,
      updated_at: at,
      body: input.bytes,
    };
    filesOf(co).push(f);
    fileEvent(co, f, "added", actor);
    return f;
  }

  /** Acme's seeded files (once per company, when its work twin has the task). */
  function seed(co: string) {
    if (seeded.has(co)) return;
    const work = deps.work();
    const tasks = work?.tasksOf(co);
    if (!tasks) return;
    seeded.add(co);
    const t = tasks.find((x) => x.title.startsWith("Screen 12 backend applicants"));
    if (!t) return;
    const m = deps.seedMembers(co);
    const owner = m.owner;
    if (!owner) return;
    const ago = (h: number) => new Date(deps.now().getTime() - h * 3_600_000).toISOString();
    const person: Event = { kind: "person", membership_id: owner };
    const agentActor = (
      m.agent ? { kind: "digital_employee", membership_id: m.agent } : person
    ) as Event;
    files.set(co, []);
    folders.set(co, []);
    const add = (
      name: string,
      bytes: Uint8Array,
      h: number,
      by: string,
      actor: Event,
      folderPath?: string,
    ) =>
      store(
        co,
        t.id,
        { bytes, name, uploader: by, at: ago(h), folderPath: folderPath ?? null },
        actor,
      );
    add(
      "Role brief - Backend engineer.pdf",
      tinyPdf("Backend engineer: role brief"),
      50,
      owner,
      person,
      "Briefs",
    );
    const chart = add("Pipeline week 43.png", chartPng(), 26, owner, person);
    if (!(chart instanceof Response)) chart.is_cover = true;
    add("Candidates.csv", new TextEncoder().encode(CANDIDATES_CSV), 8, owner, person);
    add("Shortlist.md", new TextEncoder().encode(SHORTLIST_MD), 2, m.agent ?? owner, agentActor);
  }

  // Lookups -------------------------------------------------------------------------------------

  function fileIn(p: Principal, id: string | undefined): Stored | null {
    const f = filesOf(p.company.id).find((x) => x.id === id);
    if (!f || !taskOf(p.company.id, f.task_id)) return null;
    return f;
  }

  const guard = (p: Principal | null): Response | null => {
    if (!p) return notFound();
    if (!reader(p)) return forbidden();
    return null;
  };

  const writer = (p: Principal): Response | null =>
    meOf(p) ? null : forbidden("a file is uploaded by a member");

  type Upload = {
    __files?: Record<string, { bytes: Uint8Array; name: string; type: string }>;
    text?: string;
    file_name?: string;
    task_id?: string;
    message_id?: string;
    folder_id?: string;
    folder_path?: string;
  };

  const ok = (co: string, f: Stored | Response, status = 200) =>
    f instanceof Response ? f : json(item(co, f), status);

  const handlers: Record<string, Handler> = {
    "files.list": ({ principal: p, url }) => {
      const g = guard(p);
      if (g || !p) return g as Response;
      const co = p.company.id;
      const q = Object.fromEntries(url.searchParams);
      const scopes = [q.task_id, q.board_id, q.space_id].filter(Boolean).length;
      if (scopes !== 1) return validation("name one of task_id, board_id, space_id");
      const trashed = q.trashed === "true";
      if (trashed && !q.task_id) return validation("trashed: the trash is a task's");
      if (q.task_id && !taskOf(co, q.task_id)) return notFound();
      const boardsInSpace = q.space_id
        ? new Set(
            (deps.work()?.tasksOf(co) ?? [])
              .map((t) => t.board_id)
              .filter((b) => deps.work()?.boardOf(co, b)?.space_id === q.space_id),
          )
        : null;
      const rows = filesOf(co)
        .filter((f) => (trashed ? f.deleted_at !== null : f.deleted_at === null))
        .filter((f) => {
          const t = taskOf(co, f.task_id);
          if (!t) return false;
          if (q.task_id) return f.task_id === q.task_id;
          if (q.board_id) return t.board_id === q.board_id;
          return boardsInSpace?.has(t.board_id) ?? false;
        })
        .filter((f) => !q.q || f.file_name.toLowerCase().includes(q.q.toLowerCase()))
        .sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id.localeCompare(a.id))
        .map((f) => item(co, f));
      const limit = Math.min(200, Number(q.limit ?? 50) || 50);
      const offset = Number(q.cursor ?? 0) || 0;
      return json({
        data: rows.slice(offset, offset + limit),
        next_cursor: offset + limit < rows.length ? String(offset + limit) : null,
        prev_cursor: null,
      });
    },
    "files.upload": ({ principal: p, body }) => {
      const g = guard(p);
      if (g || !p) return g as Response;
      const w = writer(p);
      if (w) return w;
      const co = p.company.id;
      const b = (body ?? {}) as Upload;
      const part = b.__files?.file;
      const name = (b.file_name ?? part?.name ?? "").trim();
      const bytes = part
        ? part.bytes
        : typeof b.text === "string"
          ? new TextEncoder().encode(b.text)
          : null;
      if (!bytes) return validation("file: a `file` or `text` part is required");
      if (!name) return validation("file_name: the file needs a name");
      for (const k of ["task_id", "message_id", "folder_id"] as const)
        if (b[k] && !UUID.test(b[k] as string)) return validation(`${k}: a uuid`);
      const taskId =
        b.task_id ?? (b.message_id ? deps.work()?.messageTask(co, b.message_id) : null);
      if (!taskId) return validation("task_id: name a task_id or a task's message_id");
      if (!taskOf(co, taskId)) return notFound();
      if (b.message_id && deps.work()?.messageTask(co, b.message_id) !== taskId)
        return validation("message_id: not a comment of this task");
      return ok(
        co,
        store(
          co,
          taskId,
          {
            bytes,
            name,
            claimed: part?.type ?? "text/plain",
            folderId: b.folder_id ?? null,
            folderPath: b.folder_path ?? null,
            messageId: b.message_id ?? null,
            uploader: meOf(p) as string,
          },
          actorOf(p),
        ),
        201,
      );
    },
    "files.get": ({ principal: p, params }) => {
      const g = guard(p);
      if (g || !p) return g as Response;
      const f = fileIn(p, params.id);
      return f ? json(item(p.company.id, f)) : notFound();
    },
    "files.content": ({ principal: p, params, url }) => {
      const g = guard(p);
      if (g || !p) return g as Response;
      const f = fileIn(p, params.id);
      if (!f) return notFound();
      if (url.searchParams.get("thumb") === "true") {
        if (previewOf(f.mime) !== "image") return notFound();
        return new Response(f.body.slice(), { status: 200, headers: { "Content-Type": f.mime } });
      }
      const inline = url.searchParams.get("download") !== "true" && previewOf(f.mime) !== "none";
      return new Response(f.body.slice(), {
        status: 200,
        headers: {
          "Content-Type": servedType(f.mime),
          "Content-Disposition": contentDisposition(inline ? "inline" : "attachment", f.file_name),
          "X-Content-Type-Options": "nosniff",
        },
      });
    },
    "files.text": ({ principal: p, params }) => {
      const g = guard(p);
      if (g || !p) return g as Response;
      const f = fileIn(p, params.id);
      if (!f) return notFound();
      const text = textual(f.mime) ? new TextDecoder().decode(f.body) : null;
      return json({ file: item(p.company.id, f), text, truncated: false });
    },
    "files.update": ({ principal: p, params, body }) => {
      const g = guard(p);
      if (g || !p) return g as Response;
      const co = p.company.id;
      const f = fileIn(p, params.id);
      if (!f) return notFound();
      if (f.deleted_at) return problem(409, "conflict", "Conflict", "the file is in the trash");
      return ok(co, change(p, f, (body ?? {}) as Record<string, unknown>));
    },
    "files.delete": ({ principal: p, params }) => {
      const g = guard(p);
      if (g || !p) return g as Response;
      const f = fileIn(p, params.id);
      if (!f) return notFound();
      trash(p, f);
      return json(item(p.company.id, f));
    },
    "files.restore": ({ principal: p, params }) => {
      const g = guard(p);
      if (g || !p) return g as Response;
      const co = p.company.id;
      const f = fileIn(p, params.id);
      if (!f) return notFound();
      if (f.deleted_at) {
        f.deleted_at = null;
        f.deleted_by_membership_id = null;
        if (f.folder_id && !foldersOf(co).some((x) => x.id === f.folder_id)) f.folder_id = null;
        f.updated_at = stamp();
        fileEvent(co, f, "restored", actorOf(p));
      }
      return json(item(co, f));
    },
    "files.emptyTrash": ({ principal: p, params }) => {
      const g = guard(p);
      if (g || !p) return g as Response;
      const co = p.company.id;
      if (!taskOf(co, params.id)) return notFound();
      const all = filesOf(co);
      const gone = all.filter((f) => f.task_id === params.id && f.deleted_at);
      files.set(
        co,
        all.filter((f) => !gone.includes(f)),
      );
      if (gone.length)
        emit(
          co,
          params.id as string,
          "task.trash_emptied",
          { count: gone.length, file_names: gone.slice(0, 50).map((f) => f.file_name) },
          actorOf(p),
        );
      return json({ purged: gone.length });
    },
    "files.folders": ({ principal: p, params }) => {
      const g = guard(p);
      if (g || !p) return g as Response;
      if (!taskOf(p.company.id, params.id)) return notFound();
      return json({ data: foldersWithCounts(p.company.id, params.id as string) });
    },
    "files.createFolder": ({ principal: p, params, body }) => {
      const g = guard(p);
      if (g || !p) return g as Response;
      const co = p.company.id;
      if (!taskOf(co, params.id)) return notFound();
      const b = (body ?? {}) as { name?: string; parent_id?: string | null };
      const name = String(b.name ?? "").trim();
      if (!name || name.includes("/") || name.length > 120)
        return validation("name: a folder name, without `/`");
      if (
        b.parent_id &&
        !foldersOf(co).some((f) => f.id === b.parent_id && f.task_id === params.id)
      )
        return validation("parent_id: not a folder of this task");
      const f = makeFolder(co, params.id as string, name, b.parent_id ?? null, actorOf(p));
      return json(folderItem(co, f), 201);
    },
    "files.updateFolder": ({ principal: p, params, body }) => {
      const g = guard(p);
      if (g || !p) return g as Response;
      const co = p.company.id;
      const f = foldersOf(co).find((x) => x.id === params.id);
      if (!f || !taskOf(co, f.task_id)) return notFound();
      const b = (body ?? {}) as { name?: string; parent_id?: string | null };
      let op: string | null = null;
      if (b.name !== undefined && b.name.trim() !== f.name) {
        const name = b.name.trim();
        if (!name || name.includes("/")) return validation("name: a folder name, without `/`");
        f.name = name;
        op = "renamed";
      }
      if (b.parent_id !== undefined && b.parent_id !== f.parent_id) {
        for (
          let cur = b.parent_id;
          cur;
          cur = foldersOf(co).find((x) => x.id === cur)?.parent_id ?? null
        )
          if (cur === f.id) return validation("parent_id: a folder cannot move under itself");
        f.parent_id = b.parent_id;
        op ??= "moved";
      }
      if (op) {
        f.updated_at = stamp();
        emit(
          co,
          f.task_id,
          "task.file_folder_changed",
          { folder_id: f.id, op, name: f.name, parent_id: f.parent_id, file_count: 0 },
          actorOf(p),
        );
      }
      return json(folderItem(co, f));
    },
    "files.deleteFolder": ({ principal: p, params }) => {
      const g = guard(p);
      if (g || !p) return g as Response;
      const co = p.company.id;
      const root = foldersOf(co).find((x) => x.id === params.id);
      if (!root || !taskOf(co, root.task_id)) return notFound();
      const doomed = subtree(co, root.id);
      let n = 0;
      for (const f of filesOf(co))
        if (f.folder_id && doomed.has(f.folder_id)) {
          f.folder_id = null;
          if (!f.deleted_at) {
            f.deleted_at = stamp();
            f.deleted_by_membership_id = meOf(p);
            n++;
            fileEvent(co, f, "trashed", actorOf(p));
          }
        }
      folders.set(
        co,
        foldersOf(co).filter((f) => !doomed.has(f.id)),
      );
      emit(
        co,
        root.task_id,
        "task.file_folder_changed",
        {
          folder_id: root.id,
          op: "removed",
          name: root.name,
          parent_id: root.parent_id,
          file_count: n,
        },
        actorOf(p),
      );
      return new Response(null, { status: 204 });
    },

    // The agent tools (Work OS shapes), for the signed-in caller.
    "agentFiles.list": ({ principal: p, url }) => {
      const g = guard(p);
      if (g || !p) return g as Response;
      const co = p.company.id;
      const taskId = url.searchParams.get("taskId");
      if (!taskId || !UUID.test(taskId)) return validation("taskId: a uuid");
      if (!taskOf(co, taskId)) return notFound();
      return json({
        data: filesOf(co)
          .filter((f) => f.task_id === taskId && !f.deleted_at)
          .sort((a, b) => a.created_at.localeCompare(b.created_at))
          .map((f) => ({
            id: f.id,
            fileName: f.file_name,
            folder: pathOf(co, f.folder_id),
            mimeType: f.mime,
            sizeBytes: f.bytes,
            uploadedByType:
              deps.nameOf(co, f.uploader_membership_id)?.kind === "person"
                ? ("human" as const)
                : ("agent" as const),
            createdAt: f.created_at,
          })),
      });
    },
    "agentFiles.read": ({ principal: p, params }) => {
      const g = guard(p);
      if (g || !p) return g as Response;
      const f = fileIn(p, params.id);
      if (!f || f.deleted_at) return notFound();
      let bin = "";
      for (const b of f.body) bin += String.fromCharCode(b);
      const image = previewOf(f.mime) === "image";
      return json({
        id: f.id,
        fileName: f.file_name,
        mimeType: f.mime,
        sizeBytes: f.bytes,
        contentBase64: btoa(bin),
        kind: image ? ("image" as const) : ("file" as const),
        text: !image && textual(f.mime) ? new TextDecoder().decode(f.body) : null,
      });
    },
    "agentFiles.upload": ({ principal: p, body }) => {
      const g = guard(p);
      if (g || !p) return g as Response;
      const w = writer(p);
      if (w) return w;
      const co = p.company.id;
      const b = (body ?? {}) as {
        taskId?: string;
        fileName?: string;
        contentBase64?: string;
        mimeType?: string;
        folderPath?: string;
      };
      if (!b.taskId || !UUID.test(b.taskId)) return validation("taskId: a uuid");
      if (!b.fileName?.trim()) return validation("fileName: required");
      if (!b.contentBase64) return validation("contentBase64: required");
      if (!taskOf(co, b.taskId)) return notFound();
      let bytes: Uint8Array;
      try {
        const bin = atob(b.contentBase64.replace(/\s/g, ""));
        bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
      } catch {
        return validation("contentBase64: not base64");
      }
      const f = store(
        co,
        b.taskId,
        {
          bytes,
          name: b.fileName,
          claimed: b.mimeType ?? null,
          folderPath: b.folderPath ?? null,
          uploader: meOf(p) as string,
        },
        actorOf(p),
      );
      if (f instanceof Response) return f;
      return json(
        { id: f.id, fileName: f.file_name, sizeBytes: f.bytes, folder: pathOf(co, f.folder_id) },
        201,
      );
    },
    "agentFiles.rename": ({ principal: p, params, body }) => {
      const g = guard(p);
      if (g || !p) return g as Response;
      const co = p.company.id;
      const f = fileIn(p, params.id);
      if (!f) return notFound();
      const newName = String((body as { newName?: string } | null)?.newName ?? "");
      const r = change(p, f, { file_name: newName });
      if (r instanceof Response) return r;
      return json({ id: r.id, fileName: r.file_name, folder: pathOf(co, r.folder_id) });
    },
    "agentFiles.delete": ({ principal: p, params }) => {
      const g = guard(p);
      if (g || !p) return g as Response;
      const f = fileIn(p, params.id);
      if (!f) return notFound();
      // A person is never held for approval (only an agent's delete is a proposal).
      trash(p, f);
      return json({ ok: true, deleted: true, approval: null });
    },
  };

  function change(p: Principal, f: Stored, b: Record<string, unknown>): Stored | Response {
    const co = p.company.id;
    const actor = actorOf(p);
    const ops: string[] = [];
    let fromName: string | null = null;
    let folderId = f.folder_id;
    if (b.folder_id !== undefined && b.folder_id !== f.folder_id) {
      if (
        b.folder_id !== null &&
        !foldersOf(co).some((x) => x.id === b.folder_id && x.task_id === f.task_id)
      )
        return validation("folder_id: not a folder of this task");
      folderId = (b.folder_id as string | null) ?? null;
    }
    if (typeof b.file_name === "string") {
      const parts = b.file_name.split("/");
      const last = (parts.pop() ?? "").trim();
      if (!last) return validation("file_name: File name cannot be empty");
      if (parts.some((s) => s.trim())) folderId = ensurePath(co, f.task_id as string, parts, actor);
      const name = cleanFileName(last);
      if (name !== f.file_name) {
        fromName = f.file_name;
        f.file_name = name;
        ops.push("renamed");
      }
    }
    if (folderId !== f.folder_id) {
      f.folder_id = folderId;
      ops.push("moved");
    }
    if (typeof b.is_cover === "boolean" && b.is_cover !== f.is_cover) {
      f.is_cover = b.is_cover;
      ops.push(b.is_cover ? "cover_set" : "cover_cleared");
    }
    if (ops.length) f.updated_at = stamp();
    for (const op of ops) fileEvent(co, f, op, actor, op === "renamed" ? fromName : null);
    return f;
  }

  function trash(p: Principal, f: Stored) {
    if (f.deleted_at) return;
    f.deleted_at = stamp();
    f.deleted_by_membership_id = meOf(p);
    f.updated_at = f.deleted_at;
    fileEvent(p.company.id, f, "trashed", actorOf(p));
  }

  function subtree(co: string, id: string): Set<string> {
    const all = foldersOf(co);
    const out = new Set([id]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const f of all)
        if (f.parent_id && out.has(f.parent_id) && !out.has(f.id)) {
          out.add(f.id);
          grew = true;
        }
    }
    return out;
  }

  function folderItem(co: string, f: Folder): FolderItem {
    const tree = subtree(co, f.id);
    return {
      ...f,
      live_files: filesOf(co).filter((x) => !x.deleted_at && x.folder_id && tree.has(x.folder_id))
        .length,
    };
  }

  const foldersWithCounts = (co: string, taskId: string) =>
    foldersOf(co)
      .filter((f) => f.task_id === taskId)
      .map((f) => folderItem(co, f));

  /** ⌘K: the caller's live files by name (the palette twin asks this). */
  function search(p: Principal, q: string, limit: number) {
    if (!reader(p) || !q) return [];
    const co = p.company.id;
    return filesOf(co)
      .filter((f) => !f.deleted_at && taskOf(co, f.task_id))
      .filter((f) => f.file_name.toLowerCase().includes(q.toLowerCase()))
      .slice(0, limit)
      .map((f) => {
        const t = taskOf(co, f.task_id);
        return {
          id: f.id,
          title: f.file_name,
          subtitle: `File · #${t?.number} ${t?.title}`,
          board_id: t?.board_id ?? null,
          task_number: t?.number ?? null,
        };
      });
  }

  /** The board card's covers (up to 4, newest first): image files marked as covers. */
  function coversOf(co: string, taskId: string): string[] {
    return filesOf(co)
      .filter((f) => f.task_id === taskId && f.is_cover && !f.deleted_at)
      .slice(0, 4)
      .map((f) => f.id);
  }

  /**
   * The API's `copyTaskFiles` (Duplicate… with Files): the task's live files and folders as new
   * rows on the copy, over the same bytes, covers kept; comment files and the trash stay behind.
   */
  function copyTaskFiles(co: string, fromTaskId: string, toTaskId: string, actor: Event): void {
    const map = new Map<string, string>();
    const pending = foldersOf(co).filter((f) => f.task_id === fromTaskId);
    while (pending.length) {
      const i = pending.findIndex((f) => !f.parent_id || map.has(f.parent_id));
      if (i < 0) break;
      const [f] = pending.splice(i, 1);
      if (!f) break;
      const at = stamp();
      const copy: Folder = {
        ...f,
        id: crypto.randomUUID(),
        task_id: toTaskId,
        parent_id: f.parent_id ? (map.get(f.parent_id) ?? null) : null,
        created_at: at,
        updated_at: at,
      };
      map.set(f.id, copy.id);
      foldersOf(co).push(copy);
    }
    for (const f of filesOf(co).filter(
      (x) => x.task_id === fromTaskId && x.subject_kind === "task" && !x.deleted_at,
    )) {
      const at = stamp();
      const copy: Stored = {
        ...f,
        id: crypto.randomUUID(),
        subject_id: toTaskId,
        task_id: toTaskId,
        folder_id: f.folder_id ? (map.get(f.folder_id) ?? null) : null,
        created_at: at,
        updated_at: at,
      };
      filesOf(co).push(copy);
      fileEvent(co, copy, "added", actor);
    }
  }

  /**
   * CHAT-FILES "Save to task": an independent copy of a chat file on a task the caller may edit
   * (its own bytes), with its provenance. A Response when it is refused.
   */
  function saveChatCopy(
    p: Principal,
    taskId: string,
    input: { bytes: Uint8Array; name: string; mime: string; folderPath: string | null },
    provenance: NonNullable<FileItem["provenance"]>,
  ): Response {
    const g = guard(p);
    if (g) return g;
    const w = writer(p);
    if (w) return w;
    const co = p.company.id;
    if (!taskOf(co, taskId)) return notFound();
    const f = store(
      co,
      taskId,
      {
        bytes: input.bytes.slice(),
        name: input.name,
        claimed: input.mime,
        folderPath: input.folderPath,
        uploader: meOf(p) as string,
      },
      actorOf(p),
    );
    if (f instanceof Response) return f;
    f.provenance = provenance;
    return json(item(co, f), 201);
  }

  return { handlers, search, coversOf, copyTaskFiles, saveChatCopy, _ctx: null as Ctx | null };
}
